import {
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  PersonAdd as ShareIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import LogoLoader from "../common/LogoLoader";
import { researchApi, ResearchProject } from "../../services/researchApi";
import ShareProjectDialog from "./ShareProjectDialog";

interface ProjectListModalProps {
  open: boolean;
  onClose: () => void;
  onLoadProject: (project: ResearchProject) => void;
  onCreateNew?: () => void;
}

const ProjectListModal: React.FC<ProjectListModalProps> = ({
  open,
  onClose,
  onLoadProject,
  onCreateNew,
}) => {
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ResearchProject | null>(
    null,
  );
  const [editProjectName, setEditProjectName] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] =
    useState<ResearchProject | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [shareProject, setShareProject] = useState<ResearchProject | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      loadProjects();
    }
  }, [open]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await researchApi.getProjects();
      setProjects(response.results);
    } catch (error) {
      console.error("Failed to load projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadProject = (project: ResearchProject) => {
    onLoadProject(project);
    onClose();
  };

  const handleEditProject = (project: ResearchProject) => {
    setEditingProject(project);
    setEditProjectName(project.name);
    setEditDialogOpen(true);
  };

  const handleUpdateProject = async () => {
    if (!editingProject || !editProjectName.trim()) return;

    try {
      setUpdating(true);
      const updatedProject = await researchApi.updateProject(
        editingProject.id,
        {
          name: editProjectName.trim(),
        },
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === editingProject.id ? updatedProject : p)),
      );

      setEditDialogOpen(false);
      setEditingProject(null);
      setEditProjectName("");
    } catch (error) {
      console.error("Failed to update project:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteProject = (project: ResearchProject) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      setDeleting(true);
      await researchApi.deleteProject(projectToDelete.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error("Failed to delete project:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicateProject = async (project: ResearchProject) => {
    try {
      setDuplicating(project.id);
      const duplicatedProject = await researchApi.duplicateProject(project.id);
      setProjects((prev) => [duplicatedProject, ...prev]);
    } catch (error) {
      console.error("Failed to duplicate project:", error);
    } finally {
      setDuplicating(null);
    }
  };

  const getProjectTypeColor = (type: string) => {
    switch (type) {
      case "discovery":
        return "#1976d2";
      case "capture":
        return "#388e3c";
      case "monitoring":
        return "#f57c00";
      case "research":
        return "#7b1fa2";
      default:
        return "#757575";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "completed":
        return "info";
      case "archived":
        return "default";
      default:
        return "default";
    }
  };

  const columns = useMemo<MRT_ColumnDef<ResearchProject>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Project Name",
        size: 250,
        Cell: ({ cell, row }) => (
          <Typography
            variant="subtitle2"
            sx={{ cursor: "pointer", color: "primary.main" }}
            onClick={() => handleLoadProject(row.original)}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: "project_type",
        header: "Type",
        size: 120,
        Cell: ({ cell }) => {
          const type = cell.getValue<string>();
          return (
            <Chip
              label={type.charAt(0).toUpperCase() + type.slice(1)}
              size="small"
              sx={{
                backgroundColor: getProjectTypeColor(type),
                color: "white",
                fontWeight: "bold",
              }}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <Chip
              label={status.charAt(0).toUpperCase() + status.slice(1)}
              size="small"
              color={getStatusColor(status) as any}
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: "created_by_username",
        header: "Owner",
        size: 120,
        Cell: ({ row }) => {
          const isOwner = row.original.permission_level === "OWNER";
          const displayName = isOwner
            ? "You"
            : row.original.created_by_username;
          const maxLen = 14;
          const truncated =
            displayName.length > maxLen
              ? displayName.slice(0, maxLen) + "…"
              : displayName;
          return (
            <Tooltip
              title={displayName}
              disableHoverListener={displayName.length <= maxLen}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {truncated}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "shared_with_count",
        header: "Shared",
        size: 100,
        Cell: ({ row }) => {
          const project = row.original;
          if (
            project.permission_level === "EDITOR" ||
            project.permission_level === "REQ_EDITOR"
          ) {
            return (
              <Chip
                icon={<GroupIcon sx={{ fontSize: 14 }} />}
                label="Shared with you"
                size="small"
                variant="outlined"
                color="info"
                sx={{ fontSize: "0.7rem", "& .MuiChip-icon": { ml: 1 } }}
              />
            );
          }
          if (project.shared_with_count > 0) {
            return (
              <Chip
                icon={<GroupIcon sx={{ fontSize: 14 }} />}
                label={String(project.shared_with_count)}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem", "& .MuiChip-icon": { ml: 1 } }}
              />
            );
          }
          return null;
        },
      },
      {
        accessorKey: "created_at",
        header: "Created",
        size: 120,
        Cell: ({ cell }) =>
          new Date(cell.getValue<string>()).toLocaleDateString(),
      },
    ],
    [],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e1e",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          Load Research Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <LogoLoader size="panel" surface="paper" label="Loading the projects" />
            </Box>
          ) : projects.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a new project to get started
              </Typography>
              {onCreateNew && (
                <Button
                  variant="contained"
                  onClick={onCreateNew}
                  sx={{ mt: 1 }}
                >
                  Create New Project
                </Button>
              )}
            </Box>
          ) : (
            <MaterialReactTable
              columns={columns}
              data={projects}
              enableRowActions
              renderRowActions={({ row }) => {
                const isOwner = row.original.permission_level === "OWNER";
                return (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {isOwner && (
                      <Tooltip title="Share project">
                        <IconButton
                          size="small"
                          onClick={() => setShareProject(row.original)}
                        >
                          <ShareIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {isOwner && (
                      <Tooltip title="Duplicate project">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicateProject(row.original)}
                          disabled={duplicating === row.original.id}
                        >
                          {duplicating === row.original.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DuplicateIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                    {isOwner && (
                      <Tooltip title="Edit project">
                        <IconButton
                          size="small"
                          onClick={() => handleEditProject(row.original)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {isOwner && (
                      <Tooltip title="Delete project">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteProject(row.original)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              }}
              muiTableBodyRowProps={{
                hover: true,
              }}
              muiTableProps={{
                sx: {
                  "& .MuiTableHead-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                  "& .MuiTableHead-root .MuiTableRow-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                  "& .MuiTableBody-root": {
                    backgroundColor: "#1e1e1e",
                  },
                  "& .MuiTableFooter-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                  "& .MuiTableFooter-root .MuiTableRow-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                  "& .MuiTableFooter-root .MuiTableCell-root": {
                    backgroundColor: "#1e1e1e !important",
                  },
                },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  backgroundColor: "#1e1e1e",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  backgroundColor: "#1e1e1e",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  backgroundColor: "#1e1e1e",
                },
              }}
              initialState={{
                pagination: { pageSize: 20, pageIndex: 0 },
                sorting: [{ id: "created_at", desc: true }],
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          {onCreateNew && (
            <Button onClick={onCreateNew} sx={{ mr: "auto" }}>
              Create New Project
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Share Project Dialog */}
      {shareProject && (
        <ShareProjectDialog
          open={!!shareProject}
          onClose={() => setShareProject(null)}
          project={shareProject}
          onShareUpdated={async () => {
            await loadProjects();
            setShareProject(null);
          }}
        />
      )}

      {/* Edit Project Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e1e",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          Edit Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <TextField
            fullWidth
            label="Project Name"
            value={editProjectName}
            onChange={(e) => setEditProjectName(e.target.value)}
            margin="normal"
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateProject}
            variant="contained"
            disabled={!editProjectName.trim() || updating}
            startIcon={updating ? <CircularProgress size={16} /> : null}
          >
            {updating ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Project Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e1e",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            Delete Project
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This action will permanently delete the project "
              {projectToDelete?.name}". This cannot be undone.
            </Typography>

            {projectToDelete && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                  borderRadius: 1,
                  border: "1px solid rgba(211, 47, 47, 0.3)",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  {projectToDelete.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {projectToDelete.project_type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created:{" "}
                  {new Date(projectToDelete.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Project"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectListModal;
